import { useState } from 'react';

import { useFormDataQuery } from '@/api/getters';
import CarCrash from '@/assets/car-crash.svg?react';
import ManChatMoney from '@/assets/man-chat-money.svg?react';
import Phone from '@/assets/phone.svg?react';
import ArrowRight from '@/assets/right-arrow.svg?react';
import SearchForDocs from '@/assets/search-for-docs.svg?react';
import StockBearish from '@/assets/stock-bearish.svg?react';
import StockBullish from '@/assets/stock-bullish.svg?react';
import Truck from '@/assets/truck.svg?react';
import UmbrellaCard from '@/assets/umbrella-card.svg?react';
import BlockOverlay from '@/components/layouts/BlockOverlay';
import AfterDoc from '@/components/ui/AfterDoc';
import Button from '@/components/ui/Button';
import { HELPFIND_PHONE_HH, PRODUCT } from '@/consts';
import { AnchoredPanel } from '@/features/v2/components/panel';
import { CONSULTANTS } from '@/features/v2/consultants';

import { useLingui } from '@lingui/react';
import { createPortal } from 'react-dom';

const FinishedAll = () => {
  const { i18n } = useLingui();
  const { data } = useFormDataQuery();
  const [closed, setClosed] = useState(false);

  const handleClose = () => {
    setClosed(true);
  };

  return (
    <>
      {data?.consultant_completed_at?.stage_III && !closed && (
        <BlockOverlayPortal handleClose={handleClose} />
      )}
      <AfterDoc
        consultant={CONSULTANTS.MARTA_ASSISTANT}
        title={i18n.t('Dziękujemy za odpowiedzi!')}
        thankYouMessage={i18n.t('Skontaktujemy się po analizie szkody')}
        productId={PRODUCT.HOUSE_HOLD}
      >
        <div className="body flex flex-col gap-2 lg:text-base">
          <span>
            {i18n.t(
              'Dziękujemy za odpowiedzi i szczegóły dotyczące szkody na nieruchomości. Teraz sprawdzimy, jaka kwota odkupu szkody jest możliwa w Twojej sprawie.'
            )}
          </span>
          <div>
            <span>{i18n.t('W ramach analizy sprawdzimy m.in.:')}</span>
            <ul className="list-disc space-y-1 pl-3.5 pt-1 lg:pl-6">
              <li>{i18n.t('czy ujęto wszystkie uszkodzenia')}</li>
              <li>
                {i18n.t('czy koszty naprawy zostały policzone rzetelnie')}
              </li>
              <li>{i18n.t('czy potrącenia są uzasadnione')}</li>
              <li>
                {i18n.t(
                  'czy zapisy polisy / OWU nie zostały zastosowane błędnie'
                )}
              </li>
              <li>{i18n.t('czy wycena została wykonana właściwie')}</li>
            </ul>
          </div>

          <section className="mt-4 lg:mt-5 lg:space-y-5">
            <h4 className="h4 mb-4">
              {i18n.t('Inne obszary, w których możemy Ci pomóc:')}
            </h4>
            <div className="max-w-80 space-y-4">
              {interestAreas.map(
                ({ content, StartElement, EndElement, href }, index) => (
                  <AnchoredPanel
                    key={index}
                    href={href}
                    startElement={<StartElement className="size-6" />}
                    endElement={
                      <EndElement className="size-4.5 transition duration-150 ease-in-out group-hover:translate-x-0.5" />
                    }
                    className="transition duration-150 ease-in-out group-hover:text-primary"
                  >
                    {content}
                  </AnchoredPanel>
                )
              )}
            </div>
          </section>
        </div>
      </AfterDoc>
    </>
  );
};

export default FinishedAll;

const BlockOverlayPortal = ({ handleClose }: { handleClose: () => void }) => {
  const { i18n } = useLingui();

  return createPortal(
    <BlockOverlay
      title={i18n.t('Zajmujemy się Twoją sprawą')}
      info={i18n.t(
        'W tym momencie nie masz możliwości udzielania kolejnych odpowiedzi, ponieważ sprawa trafiła na biurko naszych rzeczoznawców.'
      )}
      subInfo={
        <b className="font-semibold">
          {i18n.t('W przypadku pytań zapraszamy do kontaktu.')}
        </b>
      }
      onClose={handleClose}
      buttonLink={
        <Button intent={'primary'} className="disabled:bg-white" size={'small'}>
          <a href={`tel:${HELPFIND_PHONE_HH}`}>
            <Phone width={18} className="shrink-0" />
            {HELPFIND_PHONE_HH}
          </a>
        </Button>
      }
    />,
    document.body
  );
};

const interestAreas = [
  {
    StartElement: CarCrash,
    href: 'https://l.helpfind.pl/analiza-kosztorysu/?utm_source=ss-nieruchomosci-drzewko',
    content: 'Szkoda na pojeździe',
    EndElement: ArrowRight,
  },
  {
    StartElement: UmbrellaCard,
    href: 'https://l.helpfind.pl/sankcja-kredytu/?utm_source=ss-nieruchomosci-drzewko',
    content: 'Sankcja kredytu darmowego',
    EndElement: ArrowRight,
  },
  {
    StartElement: ManChatMoney,
    href: 'https://z.helpfind.pl/parabanki/?utm_source=ss-nieruchomosci-drzewko',
    content: 'Pożyczki pozabankowe',
    EndElement: ArrowRight,
  },
  {
    StartElement: StockBearish,
    href: 'https://z.helpfind.pl/polisolokaty/?utm_source=ss-nieruchomosci-drzewko',
    content: 'Zwrot z polisolokaty',
    EndElement: ArrowRight,
  },
  {
    StartElement: SearchForDocs,
    href: 'https://z.helpfind.pl/frankowicze/?utm_source=ss-nieruchomosci-drzewko',
    content: 'Unieważnij kredyt CHF',
    EndElement: ArrowRight,
  },
  {
    StartElement: Truck,
    href: 'https://z.helpfind.pl/kartel-producentow-ciezarowek/?utm_source=ss-nieruchomosci-drzewko',
    content: 'Zwrot za zakup ciężarówki',
    EndElement: ArrowRight,
  },
  {
    StartElement: StockBullish,
    href: 'https://l.helpfind.pl/wyzsza-emerytura/?utm_source=ss-nieruchomosci-drzewko',
    content: 'Prawo do wyższej emerytury',
    EndElement: ArrowRight,
  },
];
